import { AdvancedMarker, ControlPosition, Map as GoogleMap } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GoogleMapsProvider } from '@/components/map/GoogleMapsProvider';
import { googleThemeMapping } from '@/components/map/googleMapTheme';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { useTheme } from '@/context/ThemeContext';

export type LatLng = { lat: number; lng: number };

export type LocationPickerDialogProps = {
	open: boolean;
	/** Position to start from; null centres the map on the default region instead. */
	initial: LatLng | null;
	onPick: (position: LatLng) => void;
	onClose: () => void;
};

// Same fallback as the asset list map: a country-level view of Germany.
const DEFAULT_CENTER: LatLng = { lat: 51.165691, lng: 10.451526 };
const DEFAULT_ZOOM = 6;
const FOCUSED_ZOOM = 17;

function LocationPicker({ initial, onPick, onClose }: Omit<LocationPickerDialogProps, 'open'>) {
	const { t } = useTranslation();
	const { theme } = useTheme();
	const [position, setPosition] = useState<LatLng | null>(initial);

	return (
		<>
			<div className="h-[60vh] w-full overflow-hidden rounded-md border">
				<GoogleMapsProvider>
					<GoogleMap
						className="h-full w-full"
						gestureHandling="greedy"
						defaultCenter={initial ?? DEFAULT_CENTER}
						defaultZoom={initial ? FOCUSED_ZOOM : DEFAULT_ZOOM}
						mapId="DEMO_MAP_ID"
						colorScheme={googleThemeMapping[theme]}
						mapTypeControl={true}
						mapTypeControlOptions={{ position: ControlPosition.TOP_RIGHT }}
						streetViewControl={false}
						fullscreenControl={false}
						clickableIcons={false}
						minZoom={3}
						maxZoom={20}
						onClick={(event) => {
							if (event.detail.latLng) {
								setPosition(event.detail.latLng);
							}
						}}
					>
						{position && (
							<AdvancedMarker
								position={position}
								draggable
								title={t('assets.map.assetLocation')}
								onDragEnd={(event) => {
									const latLng = event.latLng;
									if (latLng) {
										setPosition({ lat: latLng.lat(), lng: latLng.lng() });
									}
								}}
							/>
						)}
					</GoogleMap>
				</GoogleMapsProvider>
			</div>
			<DialogFooter className="items-center sm:justify-between">
				<p className="text-xs text-muted-foreground tabular-nums">
					{position
						? t('assets.map.coordinatesLabel', { lat: position.lat.toFixed(6), lng: position.lng.toFixed(6) })
						: t('assets.map.noLocationSelected')}
				</p>
				<div className="flex gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						{t('common.actions.cancel')}
					</Button>
					<Button type="button" disabled={!position} onClick={() => position && onPick(position)}>
						{t('assets.map.useThisLocation')}
					</Button>
				</div>
			</DialogFooter>
		</>
	);
}

/**
 * Lets the user pick a coordinate by clicking on a map or dragging a marker.
 * The picker state lives in the dialog body, which Radix unmounts on close,
 * so every opening starts fresh from `initial`.
 */
export function LocationPickerDialog({ open, initial, onPick, onClose }: LocationPickerDialogProps) {
	const { t } = useTranslation();
	return (
		<Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
			<DialogContent className="sm:max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t('assets.form.pickOnMap')}</DialogTitle>
					<DialogDescription>{t('assets.map.pickInstructions')}</DialogDescription>
				</DialogHeader>
				<LocationPicker initial={initial} onPick={onPick} onClose={onClose} />
			</DialogContent>
		</Dialog>
	);
}
